import { Button, message, Modal, notification } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CookieService from '../services/CookieService';
import axiosInstance from '../utils/axiosConfig';

const AuthenticationModal = ({ open, onCancel, taxNo, taxAgencyAccount }) => {
    const { t } = useTranslation();

    // 认证弹窗状态
    const [qrModalOpen, setQrModalOpen] = useState(false);
    const [qrUrl, setQrUrl] = useState('');
    const [qrTitle, setQrTitle] = useState('');

    // 登录认证流程状态
    const [loginStatus, setLoginStatus] = useState(null); // null: 未查询, 0: 未登录, 1: 已登录, 2: 登录中
    const [loginLoading, setLoginLoading] = useState(false);
    const [sendLinkLoading, setSendLinkLoading] = useState(false);
    const [linkSent, setLinkSent] = useState(false);
    const loginPollingRef = useRef(null);

    // 实人认证流程状态
    const [certifyStatus, setCertifyStatus] = useState(null); // null: 未查询, 0: 未认证, 1: 已认证, 2: 认证中
    const [certifyLoading, setCertifyLoading] = useState(false);
    const [sendCertifyLinkLoading, setSendCertifyLinkLoading] = useState(false);
    const [certifyLinkSent, setCertifyLinkSent] = useState(false);
    const certifyPollingRef = useRef(null);

    // 初始化查询状态
    useEffect(() => {
        if (open && taxNo && taxAgencyAccount) {
            initializeAuthenticationStatus();
        }
    }, [open, taxNo, taxAgencyAccount]);

    // 清理轮询定时器
    useEffect(() => {
        return () => {
            if (loginPollingRef.current) {
                clearInterval(loginPollingRef.current);
            }
            if (certifyPollingRef.current) {
                clearInterval(certifyPollingRef.current);
            }
        };
    }, []);

    // 弹窗关闭时清理状态
    useEffect(() => {
        if (!open) {
            // 清理轮询
            if (loginPollingRef.current) {
                clearInterval(loginPollingRef.current);
                loginPollingRef.current = null;
            }
            if (certifyPollingRef.current) {
                clearInterval(certifyPollingRef.current);
                certifyPollingRef.current = null;
            }
            // 重置状态
            setLoginStatus(null);
            setCertifyStatus(null);
            setLinkSent(false);
            setCertifyLinkSent(false);
        }
    }, [open]);

    const initializeAuthenticationStatus = async () => {
        const token = CookieService.getToken();
        if (!token) {
            message.error(t('settings.pleaseLoginFirst'));
            return;
        }

        try {
            // 并行查询登录状态和认证状态
            const [loginResponse, certifyResponse] = await Promise.allSettled([
                axiosInstance.post('/invoice/agency/login-result', {
                    taxNo,
                    digitAccount: taxAgencyAccount
                }),
                axiosInstance.post('/invoice/agency/certify-result', {
                    taxNo,
                    digitAccount: taxAgencyAccount,
                    certifyQueryType: 1 // 获取服务端库里状态（默认）
                })
            ]);

            // 处理登录状态结果
            if (loginResponse.status === 'fulfilled') {
                if (loginResponse.value.data && loginResponse.value.data.success) {
                    setLoginStatus(loginResponse.value.data.data.loginStatus);
                    console.log('Auto login status loaded:', loginResponse.value.data.data.loginStatus);
                } else {
                    const errorMsg = loginResponse.value.data?.message || t('settings.queryLoginStatusFailed');
                    notification.error({
                        message: t('settings.queryLoginStatusFailed'),
                        description: errorMsg,
                        duration: 5
                    });
                }
            } else if (loginResponse.status === 'rejected') {
                const error = loginResponse.reason;
                const errorMsg = error?.response?.data?.message || error.message || t('settings.queryLoginStatusFailed');
                notification.error({
                    message: t('settings.queryLoginStatusFailed'),
                    description: errorMsg,
                    duration: 5
                });
            }

            // 处理认证状态结果
            if (certifyResponse.status === 'fulfilled' &&
                certifyResponse.value.data &&
                certifyResponse.value.data.success) {
                setCertifyStatus(certifyResponse.value.data.data.certificationStatus);
                console.log('Auto certify status loaded:', certifyResponse.value.data.data.certificationStatus);
            }

        } catch (error) {
            console.log('Auto query status failed:', error);
            // 静默失败，不显示错误消息
        }
    };

    const handleQueryLoginStatus = async () => {
        if (!taxNo || !taxAgencyAccount) {
            message.warning(t('settings.fillTaxInfoFirst'));
            return;
        }

        const token = CookieService.getToken();
        if (!token) {
            message.error(t('settings.pleaseLoginFirst'));
            return;
        }

        setLoginLoading(true);
        try {
            const response = await axiosInstance.post('/invoice/agency/login-result', {
                taxNo,
                digitAccount: taxAgencyAccount
            });

            if (response.data && response.data.success) {
                const newStatus = response.data.data.loginStatus;
                setLoginStatus(newStatus);
                console.log('Login status queried:', newStatus);

                // 根据状态显示相应消息
                switch (newStatus) {
                    case "1":
                        message.success(t('settings.loginStatusLoggedIn'));
                        break;
                    case "0":
                        message.info(t('settings.loginStatusNotLoggedIn'));
                        break;
                    case "2":
                        message.info(t('settings.loginStatusLoggingIn'));
                        break;
                    default:
                        message.warning(t('settings.loginStatusUnknown'));
                }
            } else {
                throw new Error(response.data?.message || t('settings.queryLoginStatusFailed'));
            }
        } catch (error) {
            console.error('Query login status error:', error);
            message.error(t('settings.queryLoginStatusFailed'));
        } finally {
            setLoginLoading(false);
        }
    };

    const handleSendLoginLink = async () => {
        if (!taxNo || !taxAgencyAccount) {
            message.error(t('settings.fillTaxInfoFirst'));
            return;
        }

        const token = CookieService.getToken();
        if (!token) {
            message.error(t('settings.pleaseLoginFirst'));
            return;
        }

        setSendLinkLoading(true);
        try {
            const response = await axiosInstance.post('/invoice/agency/login-url', {
                taxNo,
                digitAccount: taxAgencyAccount,
                businessType: '1' // 1-获取带二维码的H5链接
            });

            if (response.data && response.data.success) {
                const { url } = response.data.data;
                if (url) {
                    // 使用 iframe 模态框显示二维码
                    setQrUrl(url);
                    setQrTitle(t('settings.loginQRTitle'));
                    setQrModalOpen(true);
                    setLinkSent(true);

                    message.success(t('settings.openLoginQRSuccess'));

                    // 开始轮询登录状态
                    // startPollingLoginStatus(taxNo, taxAgencyAccount, token);
                } else {
                    message.error(t('settings.noUrlReceived'));
                }
            } else {
                message.error(t('settings.getLoginUrlFailed'));
            }
        } catch (error) {
            console.error('Get login URL failed:', error);
            const errorMsg = error.response?.data?.message || error.message || t('settings.getLoginUrlFailed');
            message.error(errorMsg);
        } finally {
            setSendLinkLoading(false);
        }
    };

    const handleQueryCertifyStatus = async () => {
        if (!taxNo || !taxAgencyAccount) {
            message.warning(t('settings.fillTaxInfoFirst'));
            return;
        }

        const token = CookieService.getToken();
        if (!token) {
            message.error(t('settings.pleaseLoginFirst'));
            return;
        }

        setCertifyLoading(true);
        try {
            const response = await axiosInstance.post('/invoice/agency/certify-result', {
                taxNo,
                digitAccount: taxAgencyAccount,
                certifyQueryType: 1 // 获取服务端库里状态（默认）
            });

            if (response.data && response.data.success) {
                const newStatus = response.data.data.certificationStatus;
                setCertifyStatus(newStatus);
                console.log('Certify status queried:', newStatus);

                // 根据状态显示相应消息
                switch (newStatus) {
                    case "1":
                        message.success(t('settings.certifyStatusCertified'));
                        break;
                    case "0":
                        message.info(t('settings.certifyStatusNotCertified'));
                        break;
                    case "2":
                        message.info(t('settings.certifyStatusCertifying'));
                        break;
                    default:
                        message.warning(t('settings.certifyStatusUnknown'));
                }
            } else {
                throw new Error(response.data?.message || t('settings.queryCertifyStatusFailed'));
            }
        } catch (error) {
            console.error('Query certify status error:', error);
            message.error(t('settings.queryCertifyStatusFailed'));
        } finally {
            setCertifyLoading(false);
        }
    };

    const handleSendCertifyLink = async () => {
        if (!taxNo || !taxAgencyAccount) {
            message.error(t('settings.fillTaxInfoFirst'));
            return;
        }

        const token = CookieService.getToken();
        if (!token) {
            message.error(t('settings.pleaseLoginFirst'));
            return;
        }

        setSendCertifyLinkLoading(true);
        try {
            const response = await axiosInstance.post('/invoice/agency/certify-url', {
                taxNo,
                digitAccount: taxAgencyAccount,
                businessType: '1', // 1-获取带二维码的H5链接
                qrCodeType: '0' // 0-税务APP(默认)
            });

            if (response.data && response.data.success) {
                const { url } = response.data.data;
                if (url) {
                    // 使用 iframe 模态框显示二维码
                    setQrUrl(url);
                    setQrTitle(t('settings.certifyQRTitle'));
                    setQrModalOpen(true);
                    setCertifyLinkSent(true);

                    message.success(t('settings.openCertifyQRSuccess'));

                    // 开始轮询认证状态
                    // startPollingCertifyStatus(taxNo, taxAgencyAccount, token);
                } else {
                    message.error(t('settings.noUrlReceived'));
                }
            } else {
                message.error(t('settings.getCertifyUrlFailed'));
            }
        } catch (error) {
            console.error('Get certify URL failed:', error);
            const errorMsg = error.response?.data?.message || error.message || t('settings.getCertifyUrlFailed');
            message.error(errorMsg);
        } finally {
            setSendCertifyLinkLoading(false);
        }
    };

    // 获取登录状态显示文本和颜色
    const getLoginStatusDisplay = () => {
        switch (loginStatus) {
            case "1":
                return { text: t('settings.loginStatusLoggedIn'), color: '#52c41a' }; // 绿色
            case "0":
                return { text: t('settings.loginStatusNotLoggedIn'), color: '#ff4d4f' }; // 红色
            case "2":
                return { text: t('settings.loginStatusLoggingIn'), color: '#faad14' }; // 黄色
            default:
                return { text: t('settings.loginStatusUnknown'), color: '#d9d9d9' }; // 灰色
        }
    };

    // 获取认证状态显示文本和颜色
    const getCertifyStatusDisplay = () => {
        switch (certifyStatus) {
            case "1":
                return { text: t('settings.certifyStatusCertified'), color: '#52c41a' }; // 绿色
            case "0":
                return { text: t('settings.certifyStatusNotCertified'), color: '#ff4d4f' }; // 红色
            case "2":
                return { text: t('settings.certifyStatusCertifying'), color: '#faad14' }; // 黄色
            default:
                return { text: t('settings.certifyStatusUnknown'), color: '#d9d9d9' }; // 灰色
        }
    };

    return (
        <>
            <Modal
                title={t('settings.authentication')}
                open={open}
                onCancel={onCancel}
                footer={[
                    <Button key="close" onClick={onCancel}>
                        {t('common.close')}
                    </Button>
                ]}
                width={600}
                destroyOnClose
            >
                <div style={{ padding: '16px 0' }}>
                    <h3 style={{ textAlign: 'left', marginBottom: 20 }}>{t('settings.digitalAccountLoginStatus')}</h3>

                    {/* 状态显示区域 */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        marginBottom: 24,
                        padding: 16,
                        backgroundColor: '#fafafa',
                        borderRadius: 6
                    }}>
                        {/* 状态指示灯 */}
                        <div style={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: getLoginStatusDisplay().color,
                            flexShrink: 0
                        }}></div>

                        <span style={{ fontSize: 16, fontWeight: 500 }}>
                            {t('settings.loginStatusText')}：<strong>{getLoginStatusDisplay().text}</strong>
                        </span>

                        {linkSent && loginStatus !== "1" && (
                            <span style={{ color: '#1890ff', fontSize: 14 }}>
                                {t('settings.loginQROpened')}
                            </span>
                        )}
                    </div>

                    {/* 操作按钮区域 */}
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: "24px" }}>
                        <Button
                            type="primary"
                            loading={loginLoading}
                            onClick={handleQueryLoginStatus}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                            }}
                            icon={<span className="material-symbols-outlined" style={{
                                fontSize: 16,
                                lineHeight: 1,
                                display: 'flex',
                                alignItems: 'center'
                            }}>refresh</span>}
                        >
                            {t('settings.refreshStatus')}
                        </Button>

                        {(loginStatus === "0" || loginStatus === "2" || loginStatus === null) && (
                            <Button
                                type={linkSent ? "default" : "primary"}
                                loading={sendLinkLoading}
                                onClick={handleSendLoginLink}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                }}
                                icon={<span className="material-symbols-outlined" style={{
                                    fontSize: 16,
                                    lineHeight: 1,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>mail</span>}
                            >
                                {linkSent ? t('settings.reopenLoginQR') : t('settings.openLoginQR')}
                            </Button>
                        )}
                    </div>

                    {/* 认证状态区域 */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        marginBottom: 24,
                        padding: 16,
                        backgroundColor: '#f6f8fa',
                        borderRadius: 6
                    }}>
                        {/* 状态指示灯 */}
                        <div style={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: getCertifyStatusDisplay().color,
                            flexShrink: 0
                        }}></div>

                        <span style={{ fontSize: 16, fontWeight: 500 }}>
                            {t('settings.certificationStatus')}：<strong>{getCertifyStatusDisplay().text}</strong>
                        </span>

                        {certifyLinkSent && certifyStatus !== "1" && (
                            <span style={{ color: '#1890ff', fontSize: 14 }}>
                                {t('settings.certifyQROpened')}
                            </span>
                        )}
                    </div>

                    {/* 认证操作按钮区域 */}
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
                        <Button
                            type="primary"
                            loading={certifyLoading}
                            onClick={handleQueryCertifyStatus}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px'
                            }}
                            icon={<span className="material-symbols-outlined" style={{
                                fontSize: 16,
                                lineHeight: 1,
                                display: 'flex',
                                alignItems: 'center'
                            }}>refresh</span>}
                        >
                            {t('settings.refreshCertifyStatus')}
                        </Button>

                        {(certifyStatus === "0" || certifyStatus === "2" || certifyStatus === null) && (
                            <Button
                                type={certifyLinkSent ? "default" : "primary"}
                                loading={sendCertifyLinkLoading}
                                onClick={handleSendCertifyLink}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                }}
                                icon={<span className="material-symbols-outlined" style={{
                                    fontSize: 16,
                                    lineHeight: 1,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>qr_code</span>}
                            >
                                {certifyLinkSent ? t('settings.reopenCertifyQR') : t('settings.openCertifyQR')}
                            </Button>
                        )}
                    </div>

                    {/* 说明文字 */}
                    <div style={{ marginTop: 20, fontSize: 14, color: '#666' }}>
                        <p style={{ margin: 0, marginBottom: 8 }}>
                            <strong>{t('settings.operationInstructions')}</strong>
                        </p>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            <li>{t('settings.instructionFillInfo')}</li>
                            <li><strong>{t('common.login')}：</strong>{t('settings.instructionLoginFlow')}</li>
                            <li><strong>{t('settings.faceVerification')}：</strong>{t('settings.instructionCertifyFlow')}</li>
                            <li>{t('settings.instructionManualRefresh')}</li>
                        </ul>
                    </div>
                </div>
            </Modal>

            {/* 二维码 iframe 模态框 */}
            <Modal
                title={qrTitle}
                open={qrModalOpen}
                onCancel={() => setQrModalOpen(false)}
                footer={[
                    <Button key="close" onClick={() => setQrModalOpen(false)}>
                        {t('common.close')}
                    </Button>
                ]}
                width={450}
                destroyOnClose
                centered
            >
                <div style={{ padding: '16px 0' }}>
                    <iframe
                        src={qrUrl}
                        width="100%"
                        height="600"
                        frameBorder="0"
                        style={{
                            border: 'none',
                            borderRadius: '6px',
                            backgroundColor: '#fff'
                        }}
                        title={qrTitle}
                        sandbox="allow-same-origin allow-scripts allow-forms"
                    />
                </div>
            </Modal>
        </>
    );
};

export default AuthenticationModal; 